import { useContext, useEffect, useState } from 'react';
import { onDropBatch, useRemote, useStorage } from '../../Utils/Utils';
import { toastDanger, toastInfo } from '../../components/popups/Toast';
import { useWebsockets } from "../../Utils/WebsocketReceiverHook";
import { useUploadWithConfirmation } from '../AdminPage/DataManagement/DataUploadWithConfirmation';
import { WorkflowContext } from './ActiveWorkflowsPage';
import LoadingIcon from '../../components/LoadingIcon';
import Divider from '../../components/Divider';
import FlexWrapper from '../FlexWrapper';
import Button from '../../components/Buttons/Button';
import DataSynchronisationCoverage from './DataSynchronisationCoverage';
import "./ActiveWorkflows.scss"


const WorkflowDataSynchronisation = ({ workflowId, taskId }) => {
    const remote = useRemote()
    const storage = useStorage()
    const sockets = useWebsockets()
    const { setPausedFiles } = useContext(WorkflowContext)

    const [data, setData] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [displayMode, setDisplayMode] = useState("default");
    const [batches, setBatches] = useState([]);
    const [onHoldFiles, setOnHoldFiles] = useState([]);

    const {
        checkBeforeUpload,
        ConfirmDialog,
    } = useUploadWithConfirmation();

    useEffect(() => {
        let isMounted = true;
         storage.getOrFetch(`/data/listBatches`).then(fetchedBatches => {
            setBatches(fetchedBatches);
         })
         remote.get(`/workflows/event/info/${workflowId}`).then(fetchedEvents => {
            const onHoldEvents = fetchedEvents.filter(event => event.event === "Batch Upload Has On Hold");
            setOnHoldFiles(onHoldEvents);
        })
        remote.get(`workflows/workflow/${workflowId}/workflowTask/${taskId}`)
            .then((res) => {
                if (isMounted) {
                    setData(res.data.data);
                    setDisplayMode(res?.data?.displayMode);
                }
            })
            .catch((err) => {
                if (isMounted) {
                    console.error(err);
                }
            });

        const socketsPath = `/api/workflow/${workflowId}/workflowTask/${taskId}`;
        const socketsPath2 = `/api/data/listBatches`;
        const socketsPath3 = `/api/workflows/event/info/${workflowId}`;
        const socketHandler = () => {
            storage.refresh(`workflows/workflow/${workflowId}/workflowTask/${taskId}`)
                .then(res => {
                    if (isMounted) {
                        setData(res.data.data);
                    }
                })
                .catch((err) => {
                    if (isMounted) {
                        console.error(err);
                    }
                });
        };

        sockets.subscribe({
            [socketsPath]: [socketHandler],
            [socketsPath2]: [getBatches],
            [socketsPath3]: [getUploads]
        });

        return () => {
            isMounted = false;
            sockets.unsubscribe([socketsPath, socketsPath2, socketsPath3]);
        };
    }, [workflowId, taskId]);

    useEffect(() => {
        if (batches && batches.length > 0 && onHoldFiles && onHoldFiles.length > 0) {
            const onHoldBatch = batches.find(batch => batch.batchId === onHoldFiles[0]?.value);
            if (onHoldBatch) {
                checkBeforeUpload(onHoldBatch);
            }
        }
    }, [onHoldFiles])

    const getUploads = () => {
        storage.refresh(`/workflows/event/info/${workflowId}`).then(fetchedEvents => {
            const onHoldEvents = fetchedEvents.filter(event => event.event === "Batch Upload Has On Hold");
            setOnHoldFiles(onHoldEvents);
            setPausedFiles(onHoldEvents.length);
        })
    };
    const getBatches = () => {
        storage.refresh(`/data/listBatches`).then(fetchedBatches => {
            setBatches(fetchedBatches);
        })
    };

    function onUploadComplete(event, response, reload) {
        const resp = response && JSON.parse(response);
        if (resp && resp.data) {
            getUploads();
            resp.data?.success > 0 && toastInfo(
                <div>
                    <p style={{ fontWeight: 600, color: "#0E8EFD" }}>Files Processing</p>
                    <p style={{ color: "#0E8EFD" }}>Your files have been added to the queue. You'll receive an email once processing is complete.</p>
                </div>, { autoClose: 5000, className: 'toast-info-align-top' }
            );
            resp.data?.failed > 0 && toastDanger(`Failed to upload ${resp.data.failed} files`);
        } else if (resp && resp.success) {
            resp.success && toastInfo(
                <div>
                    <p style={{ fontWeight: 600, color: "#0E8EFD" }}>Files Processing</p>
                    <p style={{ color: "#0E8EFD" }}>Your files have been added to the queue. You'll receive an email once processing is complete.</p>
                </div>, { autoClose: 5000, className: 'toast-info-align-top' }
            );
        } else toastDanger(`Failed to upload`)
    }

    const uploadData = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.style.display = 'none';
        fileInput.multiple = true; // Allow multiple file selection
        fileInput.addEventListener('change', (event) => {
            if (event.target.files.length > 0) {
                let formData = new FormData()
                let uploaded = [];
                for (let i = 0; i < event.target.files.length; i++) {
                    uploaded.push(event.target.files[i].name);
                    formData.append('files', event.target.files[i]);
                }
                onDropBatch(formData, `/data/upload/bulk?workflowStatusId=${workflowId}`, {
                    toastStartProcess: () => toastInfo("Uploading files", { autoClose: 5000 }),
                    onUploadComplete: onUploadComplete,
                    onUploadError: (e, xhr) => {
                        toastDanger("Upload Error");
                    },
                    setLoading: setUploading
                })
            }
        });

        document.body.appendChild(fileInput);
        fileInput.click();
    }

    if (!data) return <><LoadingIcon centered /></>

    const expectedFileCategories = data && data.requiredData ? Object.keys(data.requiredData) : []

    return (
        <section style={{ maxWidth: "1440px" }}>
            {/* Expected Files */}
            <FlexWrapper gap="s" direction="column" justify="start" align="start" style={{ marginBottom: "1rem" }} >
                <FlexWrapper gap="l" style={{ marginBottom: "0.5rem" }}>
                    {/* <p style={{ fontWeight: 600 }}>Expected Files: {expectedFileCategories?.length}/{expectedFileCategories?.length}</p> */}
                    <p style={{ fontWeight: 600 }}>Expected Data</p>
                    <Button onClick={() => uploadData()} >Import Data</Button>
                    {uploading && <LoadingIcon size={24} />}
                </FlexWrapper>
                {expectedFileCategories && expectedFileCategories.length > 0 && expectedFileCategories?.map((cat, idx) => {
                    const formattedString = cat.includes("_") ? cat.split("_").join(" ").toLowerCase() : cat.toLowerCase()
                    return <div key={"expected-files-" + idx} style={{ width: "calc(100vw - 6rem)", maxWidth: "960px", minWidth: "500px", backgroundColor: "#F2F5FD", padding: "0.5rem", borderRadius: "0.25rem", color: "#0E8EFD", fontWeight: 600, textTransform: "capitalize" }} >
                        {`${formattedString} (${data.requiredData[cat]})`}
                    </div>
                })}
            </FlexWrapper>
            <Divider />

            {/* Coverage */}
            <DataSynchronisationCoverage data={data} displayMode={displayMode} />
            <ConfirmDialog />
        </section>
    )
}

export default WorkflowDataSynchronisation